import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { MdAttachMoney, MdPix, MdCheck, MdCheckCircle, MdLocalOffer } from "react-icons/md";
import { FaCreditCard } from "react-icons/fa";
import { formatPriceWithSymbol } from "../../utils/currency";
import { getGorjetaConfig, calcularGorjeta, gorjetaAplicaEm } from "../../utils/gorjeta";
import InputMoney from "../../pages/ListItem/InputMoney";
import { registerPayment, forceCloseMesa, updateMesaDesconto } from "../../services/api";
import CustomModal from "../CustomModal";
import ModalDesconto from "../../pages/Pdv/ModalDesconto";
import "./styles.css";

const PAY_MODES = {
  TOTAL: 'total',
  SPLIT: 'split',
  ITEMS: 'items',
};

// Calcula o valor total dos adicionais de um item, respeitando regras de grupo
const calcularValorAdicionais = (grupo_adicionais) => {
  if (!grupo_adicionais || grupo_adicionais.length === 0) return 0;
  return grupo_adicionais.reduce((totalGrupo, grupo) => {
    if (grupo.calcular_maior_valor) {
      const maxVal = (grupo.adicionais || []).reduce((max, add) => {
        return add.price > max ? add.price : max;
      }, 0);
      return totalGrupo + maxVal;
    } else if (grupo.calcular_media) {
      const selecionados = (grupo.adicionais || []).filter(add => add.quantity > 0);
      if (selecionados.length > 0) {
        const soma = selecionados.reduce((acc, add) => acc + (add.price * add.quantity), 0);
        return totalGrupo + (soma / selecionados.length);
      }
      return totalGrupo;
    } else {
      const total = (grupo.adicionais || []).reduce((acc, add) => {
        return acc + (add.price * add.quantity);
      }, 0);
      return totalGrupo + total;
    }
  }, 0);
};

const ModalCloseAccount = ({ open, mesa, onClose, onPaymentSuccess, refetch }) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [splitCount, setSplitCount] = useState(2);
  const [payMode, setPayMode] = useState(PAY_MODES.TOTAL);
  const [paymentTab, setPaymentTab] = useState('');
  const { t } = useTranslation('mesas');

  const [totalPayment, setTotalPayment] = useState(0);

  // ── GORJETA ───────────────────────────────────────────────────────────────
  // Config vem do objeto `empresa` do login, sem chamada de API (mesmo desenho
  // do garcomapp/FinishMesa -- ver utils/gorjeta.js).
  const cfgGorjeta = useMemo(() => getGorjetaConfig(), []);
  const gorjetaAtiva = gorjetaAplicaEm('mesa', cfgGorjeta);
  const [incluirGorjeta, setIncluirGorjeta] = useState(true);
  const [trocoValue, setTrocoValue] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const [isForceClosing, setIsForceClosing] = useState(false);
  const [itemSplits, setItemSplits] = useState(new Map()); // key → number of parts
  const [splitPickerFor, setSplitPickerFor] = useState(null); // key with open picker
  const [desconto, setDesconto] = useState(0);
  const [showModalDesconto, setShowModalDesconto] = useState(false);
  const [isSavingDesconto, setIsSavingDesconto] = useState(false);
  const splitScrollRef = useRef(null);

  // Sincroniza desconto local com o que veio do backend ao abrir o modal
  // ou trocar de mesa.
  useEffect(() => {
    setDesconto(Number(mesa?.desconto) || 0);
  }, [mesa?._id, mesa?.desconto]);

  // Scroll horizontal com roda do mouse no carrossel de divisão
  const handleSplitWheel = useCallback((e) => {
    if (splitScrollRef.current) {
      e.preventDefault();
      splitScrollRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  // Lista base expandida por UNIDADE (antes de aplicar splits fracionários)
  const baseItems = useMemo(() => {
    if (!mesa?.pedidos) return [];
    const items = [];
    mesa.pedidos.forEach(pedidoWrapper => {
      const pedido = pedidoWrapper.id_pedido;
      if (!pedido?.itens || pedido.cancelado) return;
      pedido.itens.forEach((item, idx) => {
        const grupoAdicionais = item.grupo_adicionais || [];
        const valorAdicionais = calcularValorAdicionais(grupoAdicionais);
        const valorUnitarioComAdicionais = item.valor + valorAdicionais;
        const quantidade = item.quantidade || 1;
        for (let u = 0; u < quantidade; u++) {
          items.push({
            key: `${pedido._id}_${idx}_${u}`,
            pedido_id: pedido._id,
            item_index: idx,
            unit_index: u,
            unitLabel: quantidade > 1 ? `${u + 1}/${quantidade}` : '',
            nomeItem: item.nomeItem,
            valor: valorUnitarioComAdicionais,
            valorBase: item.valor,
            valorAdicionais,
            quantidade: 1,
            quantidadeOriginal: quantidade,
            valorTotal: valorUnitarioComAdicionais,
            grupo_adicionais: grupoAdicionais,
            obs_item: item.obs_item,
            // 'Nao informado' aqui e VALOR de dado (vai para o backend e para a
            // comanda), nao rotulo de tela. Traduzir mudaria o dado gravado.
            nome_cliente: pedido.nome_cliente || 'Não informado',
          });
        }
      });
    });
    return items;
  }, [mesa?.pedidos]);

  // Auto-detectar splits de frações já pagas (persiste entre sessões)
  const autoSplits = useMemo(() => {
    const splits = new Map();
    (mesa?.itens_pagos || []).forEach(ip => {
      if (ip.fraction_total && ip.fraction_total > 1) {
        const baseKey = `${ip.pedido_id}_${ip.item_index}_${ip.unit_index ?? 0}`;
        splits.set(baseKey, ip.fraction_total);
      }
    });
    return splits;
  }, [mesa?.itens_pagos]);

  // Merge: auto-splits (do backend) + manual splits (do usuário)
  const effectiveSplits = useMemo(() => {
    const merged = new Map(autoSplits);
    itemSplits.forEach((parts, key) => {
      if (parts > 1) merged.set(key, parts);
      else merged.delete(key);
    });
    return merged;
  }, [autoSplits, itemSplits]);

  // Lista final: aplica splits fracionários sobre baseItems
  const allItems = useMemo(() => {
    const items = [];
    for (const base of baseItems) {
      const parts = effectiveSplits.get(base.key);
      if (parts && parts > 1) {
        let acumulado = 0;
        for (let f = 0; f < parts; f++) {
          const isLast = f === parts - 1;
          const fracValor = isLast
            ? Math.round((base.valor - acumulado) * 100) / 100
            : Math.round((base.valor / parts) * 100) / 100;
          if (!isLast) acumulado += fracValor;
          items.push({
            ...base,
            key: `${base.key}_f${f}of${parts}`,
            valor: fracValor,
            valorTotal: fracValor,
            isFraction: true,
            fractionIndex: f,
            fractionTotal: parts,
            fractionLabel: `${f + 1}/${parts}`,
            originalKey: base.key,
          });
        }
      } else {
        items.push(base);
      }
    }
    return items;
  }, [baseItems, effectiveSplits]);

  // Marcar unidades/frações pagas. Compat retroativa com registros legados.
  const paidItemKeys = useMemo(() => {
    const keys = new Set();
    (mesa?.itens_pagos || []).forEach(ip => {
      if (ip.fraction_total && ip.fraction_total > 1) {
        // Fração paga
        keys.add(`${ip.pedido_id}_${ip.item_index}_${ip.unit_index ?? 0}_f${ip.fraction_index}of${ip.fraction_total}`);
      } else if (ip.unit_index !== null && ip.unit_index !== undefined) {
        keys.add(`${ip.pedido_id}_${ip.item_index}_${ip.unit_index}`);
      } else {
        const qtd = ip.quantidade || 1;
        for (let u = 0; u < qtd; u++) {
          keys.add(`${ip.pedido_id}_${ip.item_index}_${u}`);
        }
      }
    });
    return keys;
  }, [mesa?.itens_pagos]);

  // totalGeral e a fonte de verdade do modal — derivado dos pedidos populados,
  // nao do mesa.total persistido (que pode estar stale).
  const totalGeral = allItems.reduce((acc, item) => acc + item.valorTotal, 0);
  const totalJaPago = allItems
    .filter(i => paidItemKeys.has(i.key))
    .reduce((acc, i) => acc + i.valorTotal, 0);
  const totalPayedBackend = Number(mesa?.total_payed || 0);
  const totalRecebido = Math.max(totalJaPago, totalPayedBackend);
  const descontoEfetivo = Math.max(0, Math.min(Number(desconto) || 0, totalGeral));
  const faltaReceber = Math.max(0, totalGeral - descontoEfetivo - totalRecebido);

  // Base da gorjeta: o valor que ESTE pagamento vai cobrir.
  //
  // Enquanto o operador nao digitou nada, a base e o saldo da conta -- senao a
  // gorjeta so apareceria depois de digitar o valor, e o garcom escolhe a forma
  // de pagamento ANTES disso. A conta precisa mostrar a gorjeta desde o inicio.
  //
  // Incidir sobre o valor deste pagamento (e nao sobre a conta inteira) e o que
  // faz a divisao de conta ja sair rateada: cada um paga a gorjeta da sua parte.
  const baseGorjeta = Number(totalPayment) > 0 ? Number(totalPayment) : faltaReceber;
  const gorjetaValor = (gorjetaAtiva && (incluirGorjeta || cfgGorjeta.modo === 'fixa'))
    ? calcularGorjeta(baseGorjeta, cfgGorjeta)
    : 0;
  const totalComGorjeta = baseGorjeta + gorjetaValor;

  const selectedTotal = useMemo(() => {
    let sum = 0;
    selectedItems.forEach(key => {
      const item = allItems.find(i => i.key === key);
      if (item) sum += item.valorTotal;
    });
    return sum;
  }, [selectedItems, allItems]);

  const selectedPayValue = Math.min(selectedTotal, faltaReceber);

  const toggleItem = useCallback((key) => {
    if (payMode !== PAY_MODES.ITEMS) return;
    if (paidItemKeys.has(key)) return;
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [paidItemKeys, payMode]);

  const unpaidItems = allItems.filter(i => !paidItemKeys.has(i.key));
  const allUnpaidSelected = unpaidItems.length > 0 && unpaidItems.every(i => selectedItems.has(i.key));
  const isItemsMode = payMode === PAY_MODES.ITEMS;
  const isSplitMode = payMode === PAY_MODES.SPLIT;

  const toggleSelectAll = useCallback(() => {
    if (allUnpaidSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(unpaidItems.map(i => i.key)));
    }
  }, [allUnpaidSelected, unpaidItems]);

  // Dividir item em N frações
  const handleSplitItem = useCallback((baseKey, parts) => {
    setItemSplits(prev => new Map(prev).set(baseKey, parts));
    // Limpar seleção do item original (key mudou para frações)
    setSelectedItems(prev => {
      const next = new Set(prev);
      next.delete(baseKey);
      return next;
    });
    setSplitPickerFor(null);
  }, []);

  // Desfazer divisão de item
  const handleUnsplitItem = useCallback((baseKey) => {
    setItemSplits(prev => {
      const next = new Map(prev);
      next.delete(baseKey);
      return next;
    });
    // Limpar seleções das frações
    setSelectedItems(prev => {
      const next = new Set(prev);
      for (const key of prev) {
        if (key.startsWith(baseKey + '_f')) next.delete(key);
      }
      return next;
    });
  }, []);

  // Verificar se alguma fração de um item já foi paga (impede desfazer split)
  const hasAnyPaidFraction = useCallback((baseKey) => {
    for (const paidKey of paidItemKeys) {
      if (paidKey.startsWith(baseKey + '_f')) return true;
    }
    return false;
  }, [paidItemKeys]);

  const changePayMode = useCallback((mode) => {
    setPayMode(mode);
    setPaymentTab('');
    setSplitPickerFor(null);
    if (mode !== PAY_MODES.ITEMS) setSelectedItems(new Set());
    if (mode !== PAY_MODES.SPLIT) setSplitCount(2);
  }, []);

  const getPaymentValue = useCallback(() => {
    switch (payMode) {
      case PAY_MODES.SPLIT:
        return Math.round((faltaReceber / splitCount) * 100) / 100;
      case PAY_MODES.ITEMS:
        return selectedPayValue;
      default:
        return faltaReceber;
    }
  }, [payMode, faltaReceber, splitCount, selectedPayValue]);

  const openPaymentForm = (tab) => {
    const valor = getPaymentValue();
    if (valor <= 0) {
      toast.warning(t('closeAccount.nothingToPay', 'Nenhum valor para pagar'));
      return;
    }
    setPaymentTab(tab);
    setTrocoValue(0);
    setTotalPayment(valor);
  };

  const itensPorCliente = useMemo(() => {
    const map = {};
    allItems.forEach(item => {
      if (!map[item.nome_cliente]) map[item.nome_cliente] = [];
      map[item.nome_cliente].push(item);
    });
    return map;
  }, [allItems]);

  const handleProcessPayment = async () => {
    if (totalPayment > faltaReceber) {
      toast.error(t('closeAccount.maxAmount', { value: formatPriceWithSymbol(faltaReceber) }));
      return;
    }

    try {
      setIsPaying(true);
      const paidItemsPayload = [];
      if (payMode === PAY_MODES.ITEMS && selectedItems.size > 0) {
        selectedItems.forEach(key => {
          const item = allItems.find(i => i.key === key);
          if (item) {
            const entry = {
              pedido_id: item.pedido_id,
              item_index: item.item_index,
              unit_index: item.unit_index,
              nomeItem: item.nomeItem,
              valor: item.valor,
              quantidade: 1,
            };
            // Incluir dados de fração se item foi dividido
            if (item.isFraction) {
              entry.fraction_index = item.fractionIndex;
              entry.fraction_total = item.fractionTotal;
            }
            paidItemsPayload.push(entry);
          }
        });
      }

      const payment = {
        mesa_id: mesa._id,
        payment_type: paymentTab,
        total_payed: totalPayment,
        customer_name: '',
        customer_phone: '',
        paid_items: paidItemsPayload.length > 0 ? paidItemsPayload : undefined,
        desconto: descontoEfetivo,
        gorjeta: gorjetaValor > 0 ? gorjetaValor : undefined,
      };

      const response = await registerPayment(payment);

      if (response.status > 300) {
        toast.error(t('closeAccount.paymentError', 'Erro ao processar pagamento'));
        return;
      }

      if (response.data.mesa.status === 'free') {
        toast.success(t('closeAccount.paidAndReleased', 'Pagamento registrado! Mesa liberada.'));
        onPaymentSuccess?.(response.data.mesa);
        handleClose();
        refetch?.();
        return;
      }

      onPaymentSuccess?.(response.data.mesa);
      setSelectedItems(new Set());
      setPaymentTab('');
      setTotalPayment(0);
      setSplitCount(2);
      setPayMode(PAY_MODES.TOTAL);
      toast.success(t('closeAccount.partialPaid', 'Pagamento parcial registrado!'));
    } catch (error) {
      console.error(error);
      if (error.response?.data?.code === 'CAIXA_FECHADO') {
        toast.error(t('closeAccount.registerClosed', 'Caixa fechado! Abra o caixa para registrar pagamentos.'));
      } else {
        toast.error(error.response?.data?.msg || t('closeAccount.paymentError', 'Erro ao processar pagamento'));
      }
    } finally {
      setIsPaying(false);
    }
  };

  const handleClose = () => {
    setSelectedItems(new Set());
    setPaymentTab('');
    setTotalPayment(0);
    setTrocoValue(0);
    setSplitCount(2);
    setPayMode(PAY_MODES.TOTAL);
    setItemSplits(new Map());
    setSplitPickerFor(null);
    setShowModalDesconto(false);
    onClose();
  };

  const handleApplyDesconto = async (valor) => {
    if (!mesa?._id) return;
    const novoDesconto = Math.max(0, Math.min(Number(valor) || 0, totalGeral));
    try {
      setIsSavingDesconto(true);
      const response = await updateMesaDesconto(mesa._id, novoDesconto);
      if (response.status > 300) {
        toast.error(response.data?.msg || t('closeAccount.discountError', 'Erro ao aplicar desconto'));
        return;
      }
      setDesconto(novoDesconto);
      onPaymentSuccess?.(response.data.mesa);
      toast.success(novoDesconto > 0
        ? t('closeAccount.discountSaved', 'Desconto aplicado!')
        : t('closeAccount.discountRemoved', 'Desconto removido'));
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.msg || t('closeAccount.discountError', 'Erro ao aplicar desconto'));
    } finally {
      setIsSavingDesconto(false);
    }
  };

  // Destrava mesa que já foi paga mas o backend não liberou (drift de centavos).
  // Backend valida se há cobertura antes de liberar.
  const handleForceClose = async () => {
    if (!mesa?._id) return;
    try {
      setIsForceClosing(true);
      const response = await forceCloseMesa(mesa._id);
      if (response.status > 300) {
        toast.error(response.data?.msg || t('closeAccount.releaseError', 'Nao foi possivel liberar a mesa'));
        return;
      }
      toast.success(t('closeAccount.tableReleased', 'Mesa liberada!'));
      onPaymentSuccess?.(response.data.mesa);
      handleClose();
      refetch?.();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.msg || t('closeAccount.releaseTableError', 'Erro ao liberar mesa'));
    } finally {
      setIsForceClosing(false);
    }
  };

  if (!mesa) return null;

  return (
    <CustomModal
      title={`${t('closeAccount.title', 'Fechar conta')} - ${mesa.name || t('closeAccount.table', 'Mesa')}`}
      open={open}
      footer={null}
      onCancel={handleClose}
      width="580px"
      contentClassName="mca-modal-content"
    >
      <div className="mca-container">
        {/* Mode tabs */}
        <div className="mca-mode-tabs">
          <button
            className={`mca-mode-tab ${payMode === PAY_MODES.TOTAL ? 'active' : ''}`}
            onClick={() => changePayMode(PAY_MODES.TOTAL)}
          >
            {t('closeAccount.modeTotal', 'Valor total')}
          </button>
          <button
            className={`mca-mode-tab ${payMode === PAY_MODES.SPLIT ? 'active' : ''}`}
            onClick={() => changePayMode(PAY_MODES.SPLIT)}
          >
            {t('closeAccount.modeSplit', 'Dividir igual')}
          </button>
          <button
            className={`mca-mode-tab ${payMode === PAY_MODES.ITEMS ? 'active' : ''}`}
            onClick={() => changePayMode(PAY_MODES.ITEMS)}
          >
            {t('closeAccount.modeItems', 'Por itens')}
          </button>
        </div>

        {/* Discount button */}
        <button
          type="button"
          className={`mca-discount-btn ${descontoEfetivo > 0 ? 'active' : ''}`}
          onClick={() => setShowModalDesconto(true)}
          disabled={isSavingDesconto || totalGeral <= 0}
        >
          <MdLocalOffer size={16} />
          {descontoEfetivo > 0
            ? t('closeAccount.discountApplied', { value: formatPriceWithSymbol(descontoEfetivo) })
            : t('closeAccount.applyDiscount', 'Aplicar desconto')}
        </button>

        {/* Split controls */}
        {isSplitMode && (
          <div className="mca-split-panel">
            <div className="mca-split-body">
              <span>{t('closeAccount.peopleCount', 'Número de pessoas')}</span>
              <div className="mca-split-controls">
                {splitCount > 2 && (
                  <button className="mca-divide-btn" onClick={() => setSplitCount(p => p - 1)}>-</button>
                )}
                <span className="mca-divide-count">{splitCount}</span>
                <button className="mca-divide-btn" onClick={() => setSplitCount(p => p + 1)}>+</button>
              </div>
            </div>
            <div className="mca-split-result">
              <span>{t('closeAccount.amountPerPerson', 'Valor por pessoa')}</span>
              <span className="mca-split-value">
                {formatPriceWithSymbol(Math.round((faltaReceber / splitCount) * 100) / 100)}
              </span>
            </div>
          </div>
        )}

        {/* Selection toolbar */}
        {isItemsMode && unpaidItems.length > 0 && (
          <div className="mca-toolbar">
            <button
              className={`mca-select-all ${allUnpaidSelected ? 'active' : ''}`}
              onClick={toggleSelectAll}
            >
              <MdCheck size={15} />
              {allUnpaidSelected
                ? t('closeAccount.deselectAll', 'Desmarcar tudo')
                : t('closeAccount.selectAll', 'Selecionar tudo')}
            </button>
            {selectedItems.size > 0 && (
              <span className="mca-select-info">
                {t('closeAccount.itemsSelected', { count: selectedItems.size })} = {formatPriceWithSymbol(selectedTotal)}
              </span>
            )}
          </div>
        )}

        {/* Items list */}
        <div className="mca-items-scroll">
          {Object.entries(itensPorCliente).map(([nomeCliente, clienteItems]) => (
            <div key={nomeCliente} className="mca-client-section">
              <div className="mca-client-header">
                <span>{nomeCliente}</span>
              </div>
              {clienteItems.map((item) => {
                const isPaid = paidItemKeys.has(item.key);
                const isSelected = selectedItems.has(item.key);
                const canSplit = isItemsMode && !isPaid && !item.isFraction;
                const canUnsplit = isItemsMode && item.isFraction
                  && item.fractionIndex === item.fractionTotal - 1
                  && !hasAnyPaidFraction(item.originalKey)
                  && !autoSplits.has(item.originalKey); // só desfaz splits manuais
                return (
                  <React.Fragment key={item.key}>
                    <div
                      className={`mca-item ${isPaid ? 'paid' : ''} ${isItemsMode && isSelected ? 'selected' : ''} ${isItemsMode && !isPaid ? 'selectable' : ''}`}
                      onClick={() => toggleItem(item.key)}
                    >
                      {isItemsMode && (
                        <div className={`mca-check ${isPaid ? 'paid' : isSelected ? 'selected' : ''}`}>
                          {isPaid ? <MdCheckCircle size={18} /> : isSelected ? <MdCheck size={16} /> : <div className="mca-check-empty" />}
                        </div>
                      )}
                      {!isItemsMode && isPaid && (
                        <div className="mca-check paid"><MdCheckCircle size={18} /></div>
                      )}
                      <div className="mca-item-info">
                        <span className={`mca-item-name ${isPaid ? 'paid' : ''}`}>
                          {item.nomeItem}
                          {item.unitLabel && <span className="mca-item-unit"> ({item.unitLabel})</span>}
                          {item.fractionLabel && <span className="mca-item-fraction"> ({item.fractionLabel})</span>}
                        </span>
                        {item.grupo_adicionais.map((grupo, gIdx) => (
                          <div key={gIdx} className="mca-addons">
                            {(grupo.adicionais || []).map((add, aIdx) => (
                              <span key={aIdx} className="mca-addon">
                                {add.quantity}x {add.title}
                                {add.price > 0 && ` (${formatPriceWithSymbol(add.price)})`}
                              </span>
                            ))}
                          </div>
                        ))}
                        {item.obs_item && <span className="mca-obs">{t('closeAccount.itemNote', 'Obs:')} {item.obs_item}</span>}
                      </div>
                      <div className={`mca-price ${isPaid ? 'paid' : ''}`}>
                        <span>{formatPriceWithSymbol(item.valorTotal)}</span>
                        {isPaid && <span className="mca-paid-badge">{t('closeAccount.paid', 'Pago')}</span>}
                      </div>
                      {canSplit && (
                        <button
                          className="mca-split-toggle"
                          title={t('closeAccount.splitItem', 'Dividir item')}
                          onClick={(e) => { e.stopPropagation(); setSplitPickerFor(splitPickerFor === item.key ? null : item.key); }}
                        >÷</button>
                      )}
                      {canUnsplit && (
                        <button
                          className="mca-unsplit-toggle"
                          title={t('closeAccount.undoSplit', 'Desfazer divisão')}
                          onClick={(e) => { e.stopPropagation(); handleUnsplitItem(item.originalKey); }}
                        >↩</button>
                      )}
                    </div>
                    {splitPickerFor === item.key && (
                      <div className="mca-split-picker">
                        <span className="mca-split-picker-label">{t('closeAccount.splitBy', 'Dividir por:')}</span>
                        <div className="mca-split-picker-scroll" ref={splitScrollRef} onWheel={handleSplitWheel}>
                          {Array.from({ length: 19 }, (_, i) => i + 2).map(n => (
                            <button
                              key={n}
                              className="mca-split-picker-btn"
                              onClick={(e) => { e.stopPropagation(); handleSplitItem(item.key, n); }}
                            >{n}</button>
                          ))}
                        </div>
                        <button
                          className="mca-split-picker-close"
                          onClick={(e) => { e.stopPropagation(); setSplitPickerFor(null); }}
                        >✕</button>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mca-summary">
          {descontoEfetivo > 0 && (
            <>
              <div className="mca-summary-row">
                <span>{t('closeAccount.subtotal', 'Subtotal')}</span>
                <span>{formatPriceWithSymbol(totalGeral)}</span>
              </div>
              <div className="mca-summary-row discount">
                <span>{t('closeAccount.discount', 'Desconto')}</span>
                <span>-{formatPriceWithSymbol(descontoEfetivo)}</span>
              </div>
            </>
          )}
          <div className="mca-summary-row bold">
            <span>{t('closeAccount.remaining', 'Falta receber')}</span>
            <span>{formatPriceWithSymbol(faltaReceber)}</span>
          </div>

          {/* Gorjeta no RESUMO, nao junto do campo de valor: o operador escolhe a
              forma de pagamento antes de digitar qualquer coisa, e a conta precisa
              mostrar a gorjeta desde o inicio. Linha propria, nunca embutida. */}
          {gorjetaAtiva && baseGorjeta > 0 && (
            <>
              <div className="mca-summary-row mca-gorjeta-linha">
                <label className="mca-gorjeta-toggle">
                  <input
                    type="checkbox"
                    checked={incluirGorjeta || cfgGorjeta.modo === 'fixa'}
                    disabled={cfgGorjeta.modo === 'fixa'}
                    onChange={(e) => setIncluirGorjeta(e.target.checked)}
                  />
                  <span>{t('finish.tipLabel', 'Gorjeta')} ({cfgGorjeta.percentual}%)</span>
                </label>
                <span>{gorjetaValor > 0 ? formatPriceWithSymbol(gorjetaValor) : '—'}</span>
              </div>
              {gorjetaValor > 0 && (
                <div className="mca-summary-row bold mca-gorjeta-total">
                  <span>{t('finish.tipTotalToCharge', 'Total a cobrar')}</span>
                  <span>{formatPriceWithSymbol(totalComGorjeta)}</span>
                </div>
              )}
            </>
          )}
          {totalRecebido > 0 && (
            <div className="mca-summary-row received">
              <span>{t('closeAccount.alreadyReceived', 'Já recebido')}</span>
              <span>{formatPriceWithSymbol(totalRecebido)}</span>
            </div>
          )}

          {isSplitMode && (
            <div className="mca-summary-row highlight">
              <span>{t('closeAccount.splitPayment', { count: splitCount })}</span>
              <span>{formatPriceWithSymbol(Math.round((faltaReceber / splitCount) * 100) / 100)}</span>
            </div>
          )}

          {isItemsMode && selectedItems.size > 0 && (
            <div className="mca-summary-row highlight">
              <span>{t('closeAccount.itemsSelected', { count: selectedItems.size })}</span>
              <span>
                {selectedTotal > faltaReceber ? (
                  <span className="mca-value-cap">
                    <s>{formatPriceWithSymbol(selectedTotal)}</s>
                    {' '}{formatPriceWithSymbol(faltaReceber)}
                  </span>
                ) : formatPriceWithSymbol(selectedTotal)}
              </span>
            </div>
          )}
          {isItemsMode && selectedTotal > faltaReceber && (
            <div className="mca-summary-row warning">
              <span>{t('closeAccount.limitedToRemaining', 'Limitado ao valor restante da mesa')}</span>
            </div>
          )}
        </div>

        {/* Mesa liberável sem novo pagamento:
            - já paga mas não liberada (drift de centavos), ou
            - vazia/órfã (ocupada sem itens a cobrar) → permite destravar manualmente
              mesmo se a auto-cura do backend não tiver rodado ainda. */}
        {faltaReceber === 0 && (totalRecebido > 0 || totalGeral === 0) && mesa?.status !== 'free' ? (
          <div className="mca-force-close-panel">
            <p className="mca-force-close-msg">
              {totalRecebido > 0
                ? t('closeAccount.tableFullyPaid', { value: formatPriceWithSymbol(totalRecebido) })
                : t('closeAccount.noItemsToCharge', 'Esta mesa esta sem itens a cobrar. Clique abaixo para libera-la.')}
            </p>
            <button
              className="mca-submit-btn"
              disabled={isForceClosing}
              onClick={handleForceClose}
            >
              {isForceClosing
                ? t('closeAccount.releasing', 'Liberando...')
                : t('closeAccount.releaseTable', 'Liberar mesa')}
            </button>
          </div>
        ) : (
          <div className="mca-payment-buttons">
            <button
              className={`mca-pay-btn cash ${paymentTab === 'dinheiro' ? 'active' : ''}`}
              onClick={() => openPaymentForm('dinheiro')}
              disabled={isItemsMode && selectedItems.size === 0}
            >
              <MdAttachMoney size={18} />
              <span>{t('closeAccount.cash', 'Dinheiro')}</span>
            </button>
            <button
              className={`mca-pay-btn card ${paymentTab === 'cartao' ? 'active' : ''}`}
              onClick={() => openPaymentForm('cartao')}
              disabled={isItemsMode && selectedItems.size === 0}
            >
              <FaCreditCard size={14} />
              <span>{t('closeAccount.card', 'Cartão')}</span>
            </button>
            <button
              className={`mca-pay-btn pix ${paymentTab === 'pix' ? 'active' : ''}`}
              onClick={() => openPaymentForm('pix')}
              disabled={isItemsMode && selectedItems.size === 0}
            >
              <MdPix size={16} />
              <span>PIX</span>
            </button>
          </div>
        )}

        {/* Payment form */}
        {paymentTab && (
          <div className="mca-payment-form">
            {isItemsMode && selectedItems.size > 0 && (
              <div className="mca-form-info">
                <span>{t('closeAccount.itemsSelected', { count: selectedItems.size })}</span>
                <span className="mca-form-info-total">{formatPriceWithSymbol(selectedPayValue)}</span>
              </div>
            )}
            {isSplitMode && (
              <div className="mca-form-info">
                <span>{t('closeAccount.splitPart', { count: splitCount })}</span>
                <span className="mca-form-info-total">
                  {formatPriceWithSymbol(Math.round((faltaReceber / splitCount) * 100) / 100)}
                </span>
              </div>
            )}
            <div className="mca-form-field">
              <label>{t('closeAccount.enterAmount', 'Digite o valor:')}</label>
              <InputMoney value={totalPayment} onChange={(e) => {
                setTotalPayment(Math.min(Number(e.target.value), faltaReceber));
              }} />
            </div>
            {paymentTab === 'dinheiro' && (
              <div className="mca-form-field">
                <label>{t('closeAccount.changeFor', 'Troco para (opcional):')}</label>
                <InputMoney value={trocoValue} onChange={(e) => setTrocoValue(Number(e.target.value))} />
                {trocoValue > 0 && totalPayment > 0 && trocoValue > totalPayment && (
                  <p className="mca-change">{t('closeAccount.changeAmount', { value: formatPriceWithSymbol(trocoValue - totalPayment) })}</p>
                )}
              </div>
            )}
            <button
              className="mca-submit-btn"
              disabled={isPaying || totalPayment <= 0}
              onClick={handleProcessPayment}
            >
              {isPaying
                ? t('closeAccount.processing', 'Processando...')
                : t('closeAccount.pay', 'Pagar')}
            </button>
          </div>
        )}
      </div>

      <ModalDesconto
        isOpen={showModalDesconto}
        onClose={() => setShowModalDesconto(false)}
        onApply={handleApplyDesconto}
        subtotal={totalGeral}
        currentDiscount={descontoEfetivo}
      />
    </CustomModal>
  );
};

export default ModalCloseAccount;
